/* v2.4.1 – variant sync fix */
if (!customElements.get('set-form')) {
  class SetForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.submitButton = this.querySelector('[name="add"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.atcBar = document.querySelector('atc-bar');

      this.#setButtonState();

      this.querySelector('form')?.addEventListener(
        'submit',
        (event) => this.#onSubmitHandler(event)
      );

      /**
       * Update button state.
       */
      this.querySelectorAll('form input, form select').forEach(
        (input) => {
          input.addEventListener('change', (event) => {
            this.#setButtonState();
          });
        }
      );

      /**
       * Sync sizes: only the FIRST product (main case) syncs to others.
       * Uses a separate listener to avoid event cascades.
       */
      const firstProduct = this.querySelector('set-product');
      if (firstProduct) {
        firstProduct.querySelectorAll('[data-option] input[type="radio"], [data-option] select').forEach(
          (input) => {
            input.addEventListener('change', () => {
              const optionEl = input.closest('[data-option]');
              if (!optionEl) return;
              const value = input.type === 'radio' ? (input.checked ? input.value : null) : input.value;
              const optionName = optionEl.dataset.optionName || '';
              if (value) {
                this.#syncSizeToOtherProducts(firstProduct, value, optionName);
              }
            });
          }
        );
      }
    }

    /**
     * Handle form submit.
     *
     * @param {Event} event
     * @returns {Void}
     */
    #onSubmitHandler = (event) => {
      event.preventDefault();

      const isInvalid = Array.from(
        event.target.querySelectorAll('[required]')
      ).some((field) => !field.checkValidity());

      if (isInvalid) {
        alert('Please select a size!');
        return;
      }

      this.#setProductsHash();
      this.submitButton.classList.add('disabled');
      
      if (this.atcBar) {
        const submitButton = this.atcBar.querySelector('[data-submit-button]');

        submitButton?.setAttribute('disabled', true);
        submitButton?.classList.add('disabled');
      }

      /**
       * Configure request data.
       */
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(event.target);
      formData.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      /**
       * Add products to cart.
       */
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }

          this.closest('.set-modal')?.hide();
          this.cartDrawer.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');

          if (this.atcBar) {
            const submitButton = this.atcBar.querySelector('[data-submit-button]');
    
            submitButton?.removeAttribute('disabled');
            submitButton?.classList.remove('disabled');
          }

          this.dispatchEvent(
            new CustomEvent('set-form:atc-complete', {
              bubbles: true,
              details: {
                form: this
              }
            })
          );
        });
    };

    /**
     * Give each product a hash that is unique to the current set.
     *
     * @returns {Void}
     */
    #setProductsHash = () => {
      const idInputs = this.querySelectorAll('[name*=_set]:not([name*=_set-])');

      if (idInputs.length === 0) return;

      const hash = generateString(4);
      idInputs.forEach(
        (input) =>
          (input.value = input.value.split('|')[0] += `|${hash}`)
      );
    };

    /**
     * Sync size from the first product to all other products.
     * Sets values directly without dispatching change events to avoid loops.
     * Calls #setButtonState once at the end.
     */
    #syncSizeToOtherProducts = (sourceProduct, selectedValue, optionName) => {
      if (!selectedValue) return;

      const products = this.querySelectorAll('set-product');
      const sourceParts = this.#splitModelParts(selectedValue);
      const isSizeOption = (name) => /grö|size|modell|model/i.test(name);

      for (const product of products) {
        if (product === sourceProduct) continue;

        const options = product.querySelectorAll('[data-option]');
        for (const optionEl of options) {
          const optName = optionEl.dataset.optionName || '';
          if (optName !== optionName && !isSizeOption(optName)) continue;

          // Radio buttons
          const radios = optionEl.querySelectorAll('input[type="radio"]');
          if (radios.length > 0) {
            const availableRadios = Array.from(radios).filter(r => !r.disabled);
            const bestRadio = this.#findBestMatch(availableRadios, sourceParts, 'value');
            if (bestRadio) {
              bestRadio.checked = true;
              bestRadio.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              radios.forEach(r => r.checked = false);
            }
            continue;
          }

          // Select dropdown
          const select = optionEl.querySelector('select');
          if (select) {
            const availableOptions = Array.from(select.options).filter(o => !o.disabled && o.value);
            const bestOption = this.#findBestMatch(availableOptions, sourceParts, 'value');
            select.value = bestOption ? bestOption.value : '';
            if (bestOption) {
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      }

      // Update button state once after all syncs
      this.#setButtonState();
    };

    /**
     * Split a model string like "17 Pro Max / 16 Pro Max" or "14/13/13 Pro"
     * into individual model parts: ["17 Pro Max", "16 Pro Max"]
     */
    #splitModelParts(value) {
      // First split by " / " (space-slash-space = major separator)
      return value
        .split(' / ')
        .flatMap((part) => {
          // Then split by "/" but keep multi-word parts intact
          // "14/13/13 Pro" → ["14", "13", "13 Pro"]
          // "6/7/8 Plus" → ["6", "7", "8 Plus"]
          const subParts = [];
          const tokens = part.split('/');
          for (let i = 0; i < tokens.length; i++) {
            let token = tokens[i].trim();
            // If next token starts with a letter (like "Pro", "Plus", "SE2"),
            // it belongs to this token — but only if current token is numeric-ish
            // Actually, just keep them as-is; the split handles it correctly
            subParts.push(token);
          }
          return subParts;
        })
        .filter(Boolean);
    }

    /**
     * Find the element (radio/option) whose value best matches the source parts.
     * Scores by counting how many source parts appear in the candidate's parts.
     */
    #findBestMatch(elements, sourceParts, valueAttr) {
      let bestElement = null;
      let bestScore = 0;

      for (const el of elements) {
        const candidateValue = el[valueAttr] || el.getAttribute(valueAttr) || '';
        if (!candidateValue) continue;

        const candidateParts = this.#splitModelParts(candidateValue);

        // Count matching parts
        let score = 0;
        for (const sp of sourceParts) {
          if (candidateParts.includes(sp)) score++;
        }

        // Prefer exact matches, then highest overlap
        if (candidateValue === sourceParts.join(' / ') || candidateValue === sourceParts.join('/')) {
          score += 100; // exact match bonus
        }

        if (score > bestScore) {
          bestScore = score;
          bestElement = el;
        }
      }

      return bestScore > 0 ? bestElement : null;
    }

    /**
     * Set button active/disabled state based on whether
     * all products have a selected color.
     */
    #setButtonState = () => {
      const button = this.querySelector('[name="add"]');
      let hasSelectedRadios = true;
      let hasSelectedOptions = true;
      let unselectedOption = '';
      let setTotalPrice = 0;

      if (!button) return;

      const products = this.querySelectorAll('set-product');

      for (const product of products) {
        const options = product.querySelectorAll('[data-option]');
        
        setTotalPrice += parseFloat(product.getAttribute('final-price'));

        for (const option of options) {
          const hasCurrentSelectedRadios = !!option.querySelector('input:checked');
          const hasCurrentSelectedOptions = option.querySelector('select')?.value !== '';

          if (!hasCurrentSelectedRadios && !hasCurrentSelectedOptions && unselectedOption === '') {
            unselectedOption = option.dataset.optionName;
          }

          if (!hasCurrentSelectedRadios) {
            hasSelectedRadios = hasCurrentSelectedRadios;
          }

          if (!hasCurrentSelectedOptions) {
            hasSelectedOptions = hasCurrentSelectedOptions;
          }
        }
      }

      if (hasSelectedRadios || hasSelectedOptions) {
        button.innerHTML = button.dataset.enabled;

        button.removeAttribute('disabled');

        if (this.atcBar) {
          this.atcBar.querySelector('modal-opener')?.classList.add('hidden');
          this.atcBar.querySelector('[data-submit-button]')?.classList.remove('hidden');
        }
      } else {
        const priceString = Shopify.formatMoney(
          setTotalPrice,
          Shopify.current_money_format
        );

        button.innerHTML = `${window.variantStrings.chooseOption.replace('[option_name]', unselectedOption)} <small>${priceString}</small>`; 
      }
    };
  }
  customElements.define('set-form', SetForm);
}
